import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { getMapping } from '../utils/getMapping';
import {
  concernsEnum,
  enquiry,
  overallFeedback,
  sales,
} from '../constants/constants';
import { downloadExcel } from '../utils/downloadExcel';
import moment from 'moment';
import { userData } from '../recoil/atom/userData';
import { impersonateData } from '../recoil/atom/impersonate';

export const useUserDataTable = (
  npsData,
  noFeedback,
  failed,
  feedbackType,
  dateUploadCount,
  salesData,
  enquiryData
) => {
  const user = useRecoilValue(userData);
  const impersonate = useRecoilValue(impersonateData);
  const [keys, setKeys] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  useEffect(() => {
    const tableData = getJsonFromMapping(
      user?.tableMap?.[feedbackType] || user.oemMappings?.tableMap?.[feedbackType],
      npsData
    );
    setTableData(tableData);
    const allKeys = Array.from(
      new Set(tableData.flatMap((row) => Object.keys(row)))
    );
    setKeys(allKeys);
  }, [npsData, feedbackType]);

  const handleExport = () => {
    const json = getJsonFromMapping(
      user?.exportTableMap?.[feedbackType] || user.oemMappings?.exportTableMap?.[feedbackType],
      npsData
    );
    downloadExcel(json, 'nps_data');
  };

  const getJsonFromMapping = (tableMap, data) => {
    const transformedData = data.map((row) => {
      let newRow = {};
      newRow['NPS'] = getMapping(overallFeedback, row['feedback']['overall']);
      newRow['Concern'] = getMapping(concernsEnum, row['feedback']['concern']);
      newRow['Sub Concern'] = getMapping(
        concernsEnum,
        row['feedback']['sub-concern']
      );
      newRow['Feedback'] = row?.feedback?.feedbackText ?? '-';
      if (feedbackType === sales.toLowerCase()) {
        newRow['Referral Type'] = row?.['feedback']?.['referral'] || row?.['feedback']?.['referral']?.length > 0 ? row?.['feedback']?.['referral'] : 'NA';
        newRow['Referral Option'] = row?.['feedback']?.['ref-option'] || row?.['feedback']?.['ref-option']?.length > 0 ? row?.['feedback']?.['ref-option'] : 'NA';

        newRow['Referral Details'] =
          row?.['feedback']?.['referral'] === 'yes' &&
          row?.['feedback']?.['ref-option'] === 'text'
            ? row?.feedback?.feedbackText
            : '-';

        const enquiryForRetailEntry = enquiryData?.find(
          (item) => item.phone === row.phone
        );
        newRow['Enquiry NPS'] = enquiryForRetailEntry
          ? getMapping(overallFeedback, enquiryForRetailEntry.feedback.overall)
          : '-';
      }
      tableMap?.forEach((column) => {
        const value = getValueFromPath(row, column.value);
        newRow[column.title] = value;
      });
      if (feedbackType === enquiry.toLowerCase()) {
        newRow['Converted to Retail?'] = salesData.find(
          (item) => item.phone === row.phone
        )
          ? 'Yes'
          : 'No';
      }
      newRow['Message Sent On'] = moment(row['created']).format('Do MMM YYYY');

      return newRow;
    });

    return transformedData;
  };

  const getValueFromPath = (item, path) => {
    let value;
    const [objName, ...keys] = path.split('.');
    if (objName === 'user') {
      value = item[keys.join('.')];
    } else {
      value = item?.metadata[keys.join('.')];
    }
    if (path.includes('Date')) {
      const format = moment(value).format('DD-MM-YYYY');
      value = format === 'Invalid date' ? value : format;
    }
    return value;
  };

  const exportDataForNoFeedback = () => {
    const columnMappingNoFeedback = {
      'Customer Name': { key: 'name' },
      Phone: { key: 'phone' },
      'Message Sent On': { key: 'created', type: 'date' },
    };

    const transformedData =
      noFeedback &&
      noFeedback?.map((row) => {
        let newRow = {};
        Object.entries(columnMappingNoFeedback).forEach(([colName, key]) => {
          let value = row[key['key']] || 'NA';

          if (key['type'] && key['type'] === 'date' && value !== 'NA') {
            value = moment(value).format('DD-MM-YYYY');
          }
          newRow[colName] = value;
        });
        return newRow;
      });
    downloadExcel(transformedData, 'no_feedback_received_data');
  };

  const exportDataForFailed = () => {
    const columnMappingFailed = {
      'Customer Name': { key: 'name' },
      Phone: { key: 'phone' },
      'Failure Reason': { key: 'failureReason' },
      'Other Data': { key: 'metadata', type: 'object' },
    };

    const failedExcelData = failed?.map((row) => {
      let newRow = {};
      Object.entries(columnMappingFailed).forEach(([colName, key]) => {
        let value = row[key['key']] || 'NA';

        if (key['type'] && key['type'] === 'date' && value !== 'NA') {
          value = moment(value).format('DD-MM-YYYY');
        }

        if (key['type'] && key['type'] === 'object') {
          let modValue = '';
          Object.keys(value).forEach((k) => {
            modValue = modValue.concat(`${k} : ${value[k]} \n | \n`);
          });
          value = modValue;
        }
        newRow[colName] = value;
      });
      return newRow;
    });
    downloadExcel(failedExcelData, 'message_delivery_failed_data');
  };

  const handleDateWiseExport = () => {
    const transformedData = dateUploadCount.map((row) => {
      let newRow = {};
      newRow['Date'] = row['date'];
      newRow['Count'] = row['count'];

      return newRow;
    });
    downloadExcel(transformedData, 'date_wise_upload_report');
  };
  return {
    tableData,
    handleExport,
    exportDataForNoFeedback,
    exportDataForFailed,
    user: impersonate?.impersonate ? impersonate.businessDetails : user,
    handleDateWiseExport,
    page,
    handleChangePage,
    keys
  };
};
